import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from 'react-toastify';
import { useDarkMode } from '../../App';
import { FaTrash, FaPlus, FaMinus, FaShoppingBag, FaArrowRight, FaArrowLeft, FaTicketAlt } from 'react-icons/fa';
import './cart.css';

import {
  fetchCartItems,
  incrementQuantity,
  decrementQuantity,
  removeCartItem,
  removeCart,
  applyCoupon,
  removeCoupon,
  fetchCartSummary
} from "../../redux/actions/cartAction";

const Cart = () => {
  const { t, i18n } = useTranslation("global");
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { cartItems, cartSummary, status, error } = useSelector((state) => state.cart);
  const items = cartItems?.data || [];
  
  const [couponCode, setCouponCode] = useState("");

  useEffect(() => {
    dispatch(fetchCartItems(i18n.language));
    dispatch(fetchCartSummary(i18n.language));
    window.scrollTo(0, 0);
  }, [dispatch, i18n.language]);

  const handleIncrement = (cartItemId) => {
    dispatch(incrementQuantity({ cartItemId, language: i18n.language }))
      .unwrap()
      .then(() => {
        dispatch(fetchCartSummary(i18n.language));
      })
      .catch((err) => toast(err.message || t('cart.errorUpdatingQuantity')));
  };

  const handleDecrement = (cartItemId, currentQty) => {
    if (currentQty <= 1) return;
    dispatch(decrementQuantity({ cartItemId, language: i18n.language }))
      .unwrap()
      .then(() => {
        dispatch(fetchCartSummary(i18n.language));
      })
      .catch((err) => toast(err.message || t('cart.errorUpdatingQuantity')));
  };

  const handleRemoveItem = (id) => {
    dispatch(removeCartItem({ id, language: i18n.language }))
      .unwrap()
      .then(() => {
        toast.info(t('cart.itemRemoved'));
        dispatch(fetchCartSummary(i18n.language));
      })
      .catch((err) => toast(err.message || t('cart.errorRemovingItem')));
  };

  const handleClearCart = () => {
    dispatch(removeCart(i18n.language))
      .unwrap()
      .then(() => {
        toast.info(t('cart.cartCleared'));
        setCouponCode("");
      })
      .catch((err) => toast(err.message || t('cart.errorClearingCart')));
  };

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) {
      toast.warning(t('cart.enterCouponCode'));
      return;
    }
    dispatch(applyCoupon({ coupon_code: couponCode, language: i18n.language }))
      .unwrap()
      .then(() => {
        toast.success(t('cart.couponApplied'));
        setCouponCode("");
      })
      .catch((err) => {
        toast(err.message || t('cart.invalidCoupon'));
      });
  };

  const handleRemoveCoupon = () => {
    const code = cartSummary?.coupon_code;
    if (!code) return;
    dispatch(removeCoupon({ coupon_code: code, language: i18n.language }))
      .unwrap()
      .then(() => {
        toast.info(t('cart.couponRemoved'));
        dispatch(fetchCartSummary(i18n.language));
      })
      .catch((err) => toast(err.message || t('cart.errorRemovingCoupon')));
  };

  if (status === "loading" && items.length === 0) {
    return (
      <div className={`cart-page ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="cart-loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`cart-page ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Mythic Background Effects */}
      <div className="mythic-bg-blob top-left"></div>
      <div className="mythic-bg-blob bottom-right"></div>

      <div className="container py-5 cart-content-wrapper">
        {/* Header Section */}
        <div className="cart-header-section mb-5">
          <motion.div 
            className="cart-breadcrumb"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Link to="/">{t('header.home')}</Link> / <span>{t('cart.cartTitle')}</span>
          </motion.div>
          
          <div className="d-flex justify-content-between align-items-end flex-wrap gap-3">
             <div>
                <motion.h1 
                  className="cart-main-title"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {t('cart.cartTitle')}
                </motion.h1>
                <motion.p 
                  className="cart-subtitle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {items.length === 0 ? t('cart.emptyCart') : `${items.length} ${t('cart.products')} ${t('cart.inYourCart') || ''}`}
                </motion.p>
             </div>
             {items.length > 0 && (
               <motion.button 
                  className="btn btn-outline-danger rounded-pill px-4"
                  onClick={handleClearCart}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  whileHover={{ scale: 1.05 }}
               >
                 <FaTrash className="me-2" />
                 {t('cart.removeCart')}
               </motion.button>
             )}
          </div>
        </div>

        {items.length === 0 ? (
          <motion.div 
            className="empty-mythic-wrap"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="empty-icon-mythic">
              <FaShoppingBag />
            </div>
            <h2 className="empty-title-mythic">{t('cart.emptyCart')}</h2>
            <p className="text-muted mb-5 fs-5">{t('cart.noOrdersDesc')}</p>
            <Link to="/" className="btn-checkout-mythic px-5" style={{ width: 'auto', display: 'inline-flex' }}>
              <FaArrowLeft className="me-2 rtl-flip" />
              {t('cart.backToHome') || t('login.backToHome')}
            </Link>
          </motion.div>
        ) : (
          <div className="row g-5">
            {/* Cart Items List */}
            <div className="col-lg-8">
              <AnimatePresence mode="popLayout">
                {items.map((item, index) => (
                  <motion.div 
                    key={item.id}
                    className="cart-item-mythic"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    layout
                  >
                    <div className="row g-4 align-items-center">
                      <div className="col-md-auto col-4">
                        <div className="item-img-container">
                          <img 
                            src={item.product?.image || "/Frame.png"} 
                            alt={item.product?.name}
                            onError={(e) => e.target.src = "/Frame.png"}
                          />
                        </div>
                      </div>
                      
                      <div className="col-md col-8">
                        <div className="item-info-mythic">
                          <span className="item-tag-premium">{item.product?.category_name || t('cart.premium')}</span>
                          <div className="d-flex justify-content-between align-items-start">
                            <h5 className="mb-1">{item.product?.name}</h5>
                            {(item.product?.code || item.product?.sku || item.product?.item_code || item.product?.barcode) && (
                                <div className="text-muted small mb-1" style={{ fontSize: '0.85rem' }}>
                                    {t('product.code')}: {item.product?.code || item.product?.sku || item.product?.item_code || item.product?.barcode}
                                </div>
                            )}
                            <button 
                              className="btn-remove-mythic d-none d-md-flex"
                              onClick={() => handleRemoveItem(item.id)}
                            >
                              <FaTrash />
                            </button>
                          </div>
                          
                          <div className="text-muted small mb-3">
                             {item.variant && Object.entries(item.variant).filter(([k]) => k !== 'id' && k !== 'price' && k !== 'image').length > 0 && (
                               <div className="mb-1">
                                  {Object.entries(item.variant)
                                    .filter(([k]) => k !== 'id' && k !== 'price' && k !== 'image')
                                    .map(([k, v]) => <span key={k} className="me-3"><strong>{k === 'name' ? t('product.name') : k}:</strong> {v}</span>)
                                  }
                               </div>
                             )}
                             <div>
                               <strong>{t('cart.company') || 'Company'}:</strong> {item.product?.company_name || item.product?.company}
                             </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                              <div className="qty-pill-mythic">
                                <button 
                                  className="btn-qty-mythic"
                                  onClick={() => handleDecrement(item.id, item.qty)}
                                  disabled={item.qty <= 1}
                                >
                                  <FaMinus />
                                </button>
                                <span className="qty-display-mythic">{item.qty}</span>
                                <button 
                                  className="btn-qty-mythic"
                                  onClick={() => handleIncrement(item.id)}
                                >
                                  <FaPlus />
                                </button>
                              </div>
                              
                              <div className="price-mythic">
                                <span className="price-unit-mythic">
                                  {(item.price || item.unit_price || item.product?.final_price || item.product?.price || 0).toLocaleString()} {t('cart.currency')}
                                </span>
                                <span className="price-total-mythic">
                                  {((item.price || item.unit_price || item.product?.final_price || item.product?.price || 0) * item.qty).toLocaleString()} {t('cart.currency')}
                                </span>
                              </div>
                              
                              <button 
                                className="btn-remove-mythic d-flex d-md-none"
                                onClick={() => handleRemoveItem(item.id)}
                              >
                                <FaTrash />
                              </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              <motion.div 
                className="mt-5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <Link to="/" className="btn btn-link text-decoration-none p-0 text-muted fw-bold">
                  <FaArrowLeft className="me-2 rtl-flip" />
                  {t('cart.continueShopping')}
                </Link>
              </motion.div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="col-lg-4">
              <motion.div 
                className="summary-card-mythic"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h4 className="summary-title-mythic">{t('cart.orderSummary')}</h4>
                
                <div className="summary-row-mythic">
                  <span className="summary-label">{t('cart.subtotal')}</span>
                  <span className="summary-value">{(cartSummary?.sub_total || cartSummary?.subtotal || 0).toLocaleString()} {t('cart.currency')}</span>
                </div>
                
                {(cartSummary?.discount > 0 || cartSummary?.discount_amount > 0) && (
                  <div className="summary-row-mythic">
                    <span className="summary-label">{t('cart.discount')}</span>
                    <span className="summary-value text-success">-{(cartSummary.discount || cartSummary.discount_amount || 0).toLocaleString()} {t('cart.currency')}</span>
                  </div>
                )}

                <div className="summary-row-mythic">
                  <span className="summary-label">{t('cart.shipping')}</span>
                  <span className="summary-value text-success">
                    {cartSummary?.shipping_cost > 0 || cartSummary?.shipping > 0 
                      ? `${(cartSummary.shipping_cost || cartSummary.shipping).toLocaleString()} ${t('cart.currency')}`
                      : t('cart.free')}
                  </span>
                </div>

                {/* Coupon Section */}
                <div className="coupon-wrap-mythic">
                  {!cartSummary?.coupon_code ? (
                    <>
                      <input 
                        type="text" 
                        className="coupon-input-mythic"
                        placeholder={t('cart.couponPlaceholder')}
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                      />
                      <button 
                        className="btn-apply-mythic"
                        onClick={handleApplyCoupon}
                        disabled={status === "loading"}
                      >
                        {status === "loading" ? <span className="spinner-border spinner-border-sm"></span> : (t('cart.apply') || 'Apply')}
                      </button>
                    </>
                  ) : (
                    <motion.div 
                      className="applied-coupon-pill d-flex align-items-center justify-content-between p-3 rounded-4 bg-success-subtle text-success"
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                    >
                      <div className="d-flex align-items-center">
                        <FaTicketAlt className="me-2" />
                        <span className="fw-bold">{cartSummary.coupon_code}</span>
                      </div>
                      <button 
                        className="btn btn-sm text-danger border-0 h5 mb-0"
                        onClick={handleRemoveCoupon}
                      >
                        &times;
                      </button>
                    </motion.div>
                  )}
                </div>

                <div className="summary-total-row d-flex justify-content-between align-items-center">
                  <span className="total-label">{t('cart.total')}</span>
                  <div className="text-end">
                    <span className="total-value-mythic">
                      {(cartSummary?.total || cartSummary?.final_total || 0).toLocaleString()}
                    </span>
                    <span className="ms-1 fw-bold">{t('cart.currency')}</span>
                  </div>
                </div>

                <motion.button 
                  className="btn-checkout-mythic mt-5"
                  onClick={() => navigate('/checkout')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t('cart.checkout_btn') || t('cart.proceedToCheckout')}
                  <FaArrowRight className="ms-2 rtl-flip" />
                </motion.button>
              </motion.div>
              
              {/* Trust Features */}
              <div className="trust-features-mythic mt-4 row g-3 text-center">
                 <div className="col-4">
                    <div className="small text-muted mb-1">Secure</div>
                    <div className="h5 mb-0">🔒</div>
                 </div>
                 <div className="col-4">
                    <div className="small text-muted mb-1">Fast</div>
                    <div className="h5 mb-0">⚡</div>
                 </div>
                 <div className="col-4">
                    <div className="small text-muted mb-1">Support</div>
                    <div className="h5 mb-0">💬</div>
                 </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
